// client/src/pages/seo/MenuTranslationForRestaurantsEn.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";
import CountryBanner from "../../components/CountryBanner.jsx";

const sections = [
  {
    id: "start",
    label: "Menu translation for restaurants should be simple",
  },
  {
    id: "paper",
    label: "Paper menus in several languages",
  },
  {
    id: "ai",
    label: "Menu translation using AI",
  },
  {
    id: "localization",
    label: "Restaurant menu localization",
  },
  {
    id: "comparison",
    label: "Pros and cons of each option",
  },
  {
    id: "recommendation",
    label: "What should a restaurant owner choose?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps with translated menus",
  },
  {
    id: "faq",
    label: "FAQ about restaurant menu translation",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function setMetaDescription(content) {
  if (typeof document === "undefined") return;

  let meta = document.querySelector('meta[name="description"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

export default function MenuTranslationForRestaurantsEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");

    document.title =
      "Menu Translation for Restaurants: AI, Paper or QR Menu? | Best Place";

    setMetaDescription(
      "Learn how restaurants can translate their menus: paper menus, AI translation, automatic ingredient translation, menu localization and multilingual QR menus."
    );
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/menu-translation-for-restaurants",
          de: "/de/speisekarte-ubersetzen-restaurant",
          no: "/no/oversette-restaurantmeny",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Menu translation for restaurants: paper, AI or digital QR menu?
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                The biggest pain point for any foreigner dining in a restaurant 
                is the text menu. If you're looking for a solution for your 
                business, read this article to the end. You're sure to 
                find the right solution for your establishment.
              </p>
              
              <p className="seoHero__lead">
                Translating a restaurant menu sounds simple. Take the text,
                translate it, publish it. Done. In a restaurant, it is rarely
                that simple.
              </p>

              <p className="seoHero__text">
                Dishes have ingredients. Some names are local. Allergens matter.
                Guests want to understand what they order. A good translation
                makes the menu clearer. Not more complicated.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="countryBannerBlock">
        <CountryBanner
          reverse
          ariaLabel={"Countries and languages"}
        />
      </div>

      <SeoContactSection
        lang="en"
        apiOrigin={CALLBACK_API_ORIGIN}
        title="Do you need help with a translated restaurant menu?"
      />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>Menu translation for restaurants should be simple</h2>

          <p>
            Many restaurant owners start thinking about translation when guests
            ask too many questions. The guest does not understand the dish. The
            team explains the same thing again and again. At some point, it
            becomes clear: the menu needs another language.
          </p>

          <p>
            There are several approaches to this issue. You can use a printed 
            menu in multiple languages, use AI translation services, or use 
            a digital {" "}
            <Link
              to="/en/multilingual-menu-for-restaurants"
              className="seoAccentLink"
            >
              multilingual menu for restaurants
            </Link>{" "}
            with translation capabilities.
          </p>

          <p>
            Not every solution fits every restaurant. A small café may only need
            English. A restaurant near hotels may need several languages. A
            tourist location may need even more. The goal is not the maximum
            number of languages. The goal is to choose the right languages for
            real guests.
          </p>
        </section>

        <section id="paper" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Paper menus in several languages</h2>

          <p>
            A classic paper menu is a must. Guests are accustomed to paper 
            menus. But translating them can be a real challenge. It might 
            seem like printing a menu in two or three languages ​​is all it 
            takes. For some restaurants, that's enough. Staff don't need to 
            be told anything. However, adding even one language already 
            doubles the menu, and if two or three languages ​​are needed, 
            the task becomes impossible.
          </p>

          <p>
            There's another option, and in my opinion, it's a pretty good 
            solution. Simply have multiple menus in different languages. 
            So, if an Italian comes, we give you the Italian version of the 
            menu, and if a German comes, we give you the German version. 
            This is ideal for guests—they're simply given a menu in their 
            language. However, it's important to understand that the 
            difficulties multiply significantly when:
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">
              Several languages make printed menus longer.
            </li>
            <li className="text__seo-li">
              Updates cost time and printing money.
            </li>
            <li className="text__seo-li">
              Small text becomes hard to read.
            </li>
            <li className="text__seo-li">
              Old translations often stay in use too long.
            </li>
            <li className="text__seo-li">
              Allergens and ingredients need careful checking in every language.
            </li>
          </ul>

          <p>
            Paper is not wrong. It is just not flexible. It works best for short
            menus that rarely change.
          </p>
          </div>
        </section>

        <section id="ai" className="acquaintance">
          <h2>Menu translation using AI</h2>

          <p>
            We're all used to the fact that translating text using artificial 
            intelligence has become incredibly easy. However, when it comes 
            to translating a restaurant menu, things aren't so 
            straightforward. It can quickly create a rough draft. 
            It can write simple descriptions. Translation difficulties 
            usually arise when it comes to translating national dishes or 
            new products. Automatic translation of ingredients is 
            especially interesting. Here, too, many meanings and nuances 
            can arise.
          </p>

          <p>
            The formula for using AI-powered menu translation is essentially
             simple. I recommend using AI for the first draft. Then, check 
             the names of dishes, ingredients, allergens, and regional 
             specifics. Only publish.
          </p>

          <p>
            It's also worth mentioning that there are AI translation services 
            that can be used to host restaurant menus. These services allow 
            guests to select a language built into the service, and the AI 
            ​​will automatically translate it.
          </p>

          <VisualIdea>           
            To summarize, translating a menu using AI is quick and 
            fairly simple, but it should be taken into account that 
            some parts of the text will not be translated correctly 
            in the guest's language.
          </VisualIdea>
        </section>

        <section id="localization" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Restaurant menu localization</h2>

            <p>
              If you want your restaurant menu translation to be more than 
              just a word swap, you need to approach it with special attention. 
              A good menu translation is localization. Guests should understand 
              what they want to try, even if they're unfamiliar with the local 
              cuisine.
            </p>

            <p>
              Example: a regional dish name may be obvious for local guests. For
              tourists, it may mean nothing. In that case, a short description is
              better than a direct translation. What is inside? How is it served?
              Is it spicy? Is it vegetarian? Are there allergens?
            </p>

            <p>
              There are services that undertake high-quality translation of 
              restaurant menus. This is a fairly extensive process, as it 
              requires the involvement of relevant specialists—linguists.
            </p>
          </div>
        </section>

        <section id="comparison" className="acquaintance">
          <h2>Pros and cons of each option</h2>

            <p>
              There is no perfect solution for every restaurant. The right choice
              depends on how often the menu changes and how many international
              guests come.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Weaker for updates</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Paper menus need reprinting after changes.
                  </li>
                  <li className="text__seo-li">
                    Camera apps give the restaurant no control.
                  </li>
                  <li className="text__seo-li">
                    AI translations need review.
                  </li>
                  <li className="text__seo-li">
                    Separate PDF files become hard to manage.
                  </li>
                  <li className="text__seo-li">
                    Several language versions are hard to keep up to date.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better for flexible restaurants</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    One digital menu can contain several languages.
                  </li>
                  <li className="text__seo-li">
                    Guests choose the language on the smartphone.
                  </li>
                  <li className="text__seo-li">
                    Prices and descriptions can be updated online.
                  </li>
                  <li className="text__seo-li">
                    Ingredients and allergens can be reviewed per language.
                  </li>
                  <li className="text__seo-li">
                    The same QR code can stay in use after changes.
                  </li>
                </ul>
              </div>
            </div>
        </section>

        <section id="recommendation" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should a restaurant owner choose?</h2>

            <p>
              If your menu almost never changes and you only need a second
              language, a printed menu can be enough. Keep it simple.
            </p>

            <p>
              If your restaurant often changes prices, dishes or daily offers, a
              digital menu is usually more practical. You do not need to print
              again every time.
            </p>

            <p>
              If you serve tourists, hotel guests or international visitors,
              start with the languages you really hear in your restaurant.
              English is often the first step. Then add the next language based
              on real demand.
            </p>

            <p>
              A good next step is to test a{" "}
              <Link
                to="/en/multilingual-menu-for-restaurants"
                className="seoAccentLink"
              >
                multilingual QR menu
              </Link>
              . Check it on a smartphone. Ask the team. Watch whether guests
              understand dishes faster. Then expand.
            </p>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section className="acquaintance">
          <h2>How Best Place helps with translated menus</h2>

          <p>
            Best Place helps restaurant owners create a digital menu in several
            languages. The restaurant keeps one clear structure. The guest opens
            the menu with a QR code and chooses the language they need.
          </p>

          <p>
            This is useful when a restaurant wants to look more professional for
            international guests, reduce repeated questions and update the menu
            without new print files.
          </p>

          <p>
            You can start from the{" "}
            <Link 
            to="/en"
            className="seoAccentLink"
            >
              Best Place landing page</Link>, create a simple menu
            and test it directly on a real smartphone.
          </p>
        </section>

        <section id="faq" className="acquaintance">
          <h2>FAQ about restaurant menu translation</h2>

          <div className="seoFaq">
            <details>
              <summary>What does restaurant menu translation mean?</summary>

              <p>
                It means translating dish names, descriptions, categories,
                prices, ingredients and important notes into another language so
                guests can understand the menu more easily.
              </p>
            </details>

            <details>
              <summary>
                How do I translate a café menu into English correctly?
              </summary>

              <p>
                Start with the most important items. Do not translate only names.
                Add short descriptions, ingredients, sizes, milk options,
                allergens and extras where they help the guest decide.
              </p>
            </details>

            <details>
              <summary>
                Can AI automatically translate ingredients and dishes?
              </summary>

              <p>
                Yes, AI can create a first draft. But ingredients, allergens,
                regional terms and sensitive notes should always be reviewed.
              </p>
            </details>

            <details>
              <summary>What does restaurant menu localization mean?</summary>

              <p>
                Localization means the menu is not only translated word by word.
                The description is adapted so guests from other countries can
                really understand the dish.
              </p>
            </details>

            <details>
              <summary>
                Is a multilingual QR menu better than several PDFs?
              </summary>

              <p>
                In many cases, yes. A multilingual QR menu is easier to update,
                cleaner on a smartphone and more flexible than several separate
                PDF files.
              </p>
            </details>

            <details>
              <summary>
                Which language should a restaurant add first?
              </summary>

              <p>
                Start with the language your international guests use most often.
                For many restaurants, English is the first useful additional
                language.
              </p>
            </details>
          </div>
        </section>
      </article>
    </main>
  );
}